import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, DollarSign, Briefcase, Tag, Check, Bookmark } from "lucide-react";
import { useI18n } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import useSeo from "../hooks/useSeo";

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const fmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  useSeo({
    title: job ? `${job.title?.[lang] || job.title?.en} - ${job.company}` : "Job Detail",
    description: job ? `${(job.description?.[lang] || job.description?.en || "").slice(0, 160)}` : "Job opening at ENCES Global.",
    path: `/job/${id}`,
  });

  useEffect(() => { api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => navigate("/jobs")); }, [id, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/saved-jobs").then((r) => setIsSaved(r.data.some((x) => x.job_id === id))).catch(() => {});
  }, [user, id]);

  const onApply = () => {
    if (!user) { toast.info(t.job.needSignin); navigate("/login", { state: { from: `/apply/${id}` } }); return; }
    navigate(`/apply/${id}`);
  };

  const toggleSave = async () => {
    if (!user) { toast.info(t.job.needSignin); navigate("/login"); return; }
    try {
      if (isSaved) { await api.delete(`/saved-jobs/${id}`); setIsSaved(false); }
      else { await api.post(`/saved-jobs/${id}`); setIsSaved(true); }
    } catch { toast.error("Error"); }
  };

  if (!job) return <div className="container-pad py-32 text-center text-slate-500">{t.common.loading}</div>;

  return (
    <div data-testid="page-job-detail" className="container-pad mx-auto max-w-[1100px] py-12 md:py-16">
      <Link to="/jobs" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-navy mb-6" data-testid="back-to-jobs">
        <ArrowLeft size={16} /> {t.common.goBack}
      </Link>

      <div className="rounded-[28px] bg-navy text-white p-8 md:p-10">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="px-3 py-1 rounded-full bg-white/10">{job.category}</span>
          <span className="px-3 py-1 rounded-full bg-brandred">{job.type}</span>
        </div>
        <h1 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tight" data-testid="job-title">{job.title[lang] || job.title.en}</h1>
        <div className="mt-2 text-white/70">{job.company}</div>
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3 text-sm text-white/80">
          <span className="inline-flex items-center gap-2"><MapPin size={16} /> {job.location}</span>
          <span className="inline-flex items-center gap-2"><Briefcase size={16} /> {job.type}</span>
          <span className="inline-flex items-center gap-2"><Tag size={16} /> {job.category}</span>
          <span className="inline-flex items-center gap-2 font-bold text-white"><DollarSign size={16} /> {fmt.format(job.salary_min)} – {fmt.format(job.salary_max)}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10">
        <div className="md:col-span-2 space-y-8">
          <section>
            <h2 className="text-xl font-bold text-navy">{t.job.description}</h2>
            <p className="mt-3 text-slate-700 leading-relaxed">{job.description[lang] || job.description.en}</p>
          </section>
          <section>
            <h2 className="text-xl font-bold text-navy">{t.job.requirements}</h2>
            <ul className="mt-3 space-y-2">
              {(job.requirements[lang] || job.requirements.en).map((r, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-navy" /> <span>{r}</span>
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h2 className="text-xl font-bold text-navy">{t.job.benefits}</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {(job.benefits[lang] || job.benefits.en).map((b, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-50 text-green-700 text-sm font-semibold">
                  <Check size={14} /> {b}
                </span>
              ))}
            </div>
          </section>
        </div>

        <aside className="md:sticky md:top-28 self-start bg-soft rounded-[24px] p-6">
          <div className="text-xs uppercase tracking-widest text-slate-500">{t.job.apply}</div>
          <div className="mt-2 font-bold text-navy text-lg">{job.title[lang] || job.title.en}</div>
          <div className="text-sm text-slate-600">{job.company}</div>
          <button onClick={onApply} className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brandred hover:bg-red-700 text-white font-semibold shadow-lg shadow-red-500/20" data-testid="job-apply-button">{t.common.applyNow}</button>
          <button onClick={toggleSave} className={`mt-3 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold border ${isSaved ? "bg-navy text-white border-navy" : "border-navy text-navy hover:bg-navy hover:text-white"} transition-colors`} data-testid="job-save-button">
            <Bookmark size={16} /> {isSaved ? t.common.saved : t.common.save}
          </button>
        </aside>
      </div>

      {/* Sticky mobile CTA */}
      <div className="fixed bottom-4 left-4 right-4 md:hidden z-40">
        <button onClick={onApply} className="w-full px-6 py-4 rounded-full bg-brandred text-white font-semibold shadow-2xl" data-testid="job-apply-mobile">{t.common.applyNow}</button>
      </div>
    </div>
  );
}
